import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Check, X } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import { useTenantInvoiceStore } from '../../../store/useTenantInvoiceStore';
import { useAdminTenantStore } from '../../../store/useAdminTenantStore';
import {
  TenantInvoiceStatus,
  type TenantInvoiceDto,
} from '../../../api/types/admin';
import { formatDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/money';
import { formatPercent } from '../../../utils/percent';

function statusVariant(s: number | undefined): 'default' | 'info' | 'success' | 'danger' {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'info';
    case TenantInvoiceStatus.Paid:
      return 'success';
    case TenantInvoiceStatus.Cancelled:
      return 'danger';
    default:
      return 'default';
  }
}

function statusLabelKey(s: number | undefined): string {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'issued';
    case TenantInvoiceStatus.Paid:
      return 'paid';
    case TenantInvoiceStatus.Cancelled:
      return 'cancelled';
    default:
      return 'draft';
  }
}

export default function TenantInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);

  const { saving, downloadingPdf, getById, downloadPdf, markPaid, cancel } =
    useTenantInvoiceStore();
  const { tenants, fetchAll: fetchTenants } = useAdminTenantStore();

  const [invoice, setInvoice] = useState<TenantInvoiceDto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const inv = await getById(id);
      if (!cancelled) {
        setInvoice(inv);
        setLoaded(true);
      }
      if (tenants.length === 0) fetchTenants();
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getById, fetchTenants, tenants.length]);

  if (!loaded) {
    return <p className="text-[13px] text-slate-500 text-center py-12">{t('common:actions.loading')}</p>;
  }

  if (!invoice) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('admin:tenantInvoices.errors.notFound')}
          action={
            <Link to="/admin/tenant-invoices">
              <Button variant="secondary" leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}>
                {t('admin:tenantInvoices.actions.back')}
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const tenantName = invoice.tenantId
    ? tenants.find((tn) => tn.id === invoice.tenantId)?.name ?? invoice.tenantId
    : '—';

  const money = (value: number | undefined | null) =>
    formatMoney(value, { currency: invoice.currency });

  const handleMarkPaid = async () => {
    if (!invoice.id) return;
    const ok = await markPaid(invoice.id);
    setConfirmPaid(false);
    if (ok) {
      const fresh = await getById(invoice.id);
      setInvoice(fresh);
    }
  };

  const handleCancel = async () => {
    if (!invoice.id) return;
    const ok = await cancel(invoice.id);
    setConfirmCancel(false);
    if (ok) {
      const fresh = await getById(invoice.id);
      setInvoice(fresh);
    }
  };

  const positions = (invoice.positions ?? []).slice().sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/tenant-invoices')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('admin:tenantInvoices.actions.back')}
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 font-mono truncate">
                {invoice.invoiceNumber || t('admin:tenantInvoices.fields.draft')}
              </h1>
              <Badge variant={statusVariant(invoice.status)}>
                {t(`admin:tenantInvoices.status.${statusLabelKey(invoice.status)}`)}
              </Badge>
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {tenantName} · {formatDate(invoice.periodStart)} – {formatDate(invoice.periodEnd)}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 shrink-0">
            <Button
              variant="secondary"
              onClick={() => invoice.id && downloadPdf(invoice.id, invoice.invoiceNumber)}
              disabled={downloadingPdf}
              leftIcon={<Download className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto justify-center"
            >
              {t('admin:tenantInvoices.actions.downloadPdf')}
            </Button>
            {invoice.status === TenantInvoiceStatus.Issued && (
              <Button
                onClick={() => setConfirmPaid(true)}
                disabled={saving}
                leftIcon={<Check className="w-4 h-4" strokeWidth={2} />}
                className="w-full sm:w-auto justify-center"
              >
                {t('admin:tenantInvoices.actions.markPaid')}
              </Button>
            )}
            {invoice.status !== TenantInvoiceStatus.Cancelled && (
              <Button
                variant="danger"
                onClick={() => setConfirmCancel(true)}
                disabled={saving}
                leftIcon={<X className="w-4 h-4" strokeWidth={2} />}
                className="w-full sm:w-auto justify-center"
              >
                {t('admin:tenantInvoices.actions.cancel')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-[13px]">
          <Row label={t('admin:tenantInvoices.fields.tenant')} value={tenantName} />
          <Row
            label={t('admin:tenantInvoices.fields.period')}
            value={`${formatDate(invoice.periodStart)} – ${formatDate(invoice.periodEnd)}`}
          />
          <Row
            label={t('admin:tenantInvoices.fields.issueDate')}
            value={invoice.issueDate ? formatDate(invoice.issueDate) : '—'}
          />
          <Row
            label={t('admin:tenantInvoices.fields.dueDate')}
            value={invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
          />
        </dl>
      </Card>

      <Card title={t('admin:tenantInvoices.fields.positionsHeader')} noPadding>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-12">#</TableHeader>
              <TableHeader>{t('admin:tenantInvoices.fields.positionDescription')}</TableHeader>
              <TableHeader className="text-right">
                {t('admin:tenantInvoices.fields.positionAmount')}
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos, idx) => (
              <TableRow key={pos.id ?? idx}>
                <TableCell className="text-slate-500">{idx + 1}</TableCell>
                <TableCell>{pos.description}</TableCell>
                <TableCell
                  className={`text-right font-mono ${pos.isCredit ? 'text-red-600' : ''}`}
                >
                  {pos.isCredit ? '−\u00A0' : ''}
                  {money(pos.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card>
        <dl className="space-y-1 text-[13px]">
          <Row label={t('admin:tenantInvoices.fields.netAmount')} value={money(invoice.netAmount)} />
          <Row
            label={`${t('admin:tenantInvoices.fields.vatAmount')} (${formatPercent((invoice.vatRate ?? 0) * 100)})`}
            value={money(invoice.vatAmount)}
          />
          <Row
            label={t('admin:tenantInvoices.fields.grossAmount')}
            value={
              <span className="text-[16px] font-mono font-semibold text-slate-900">
                {money(invoice.grossAmount)}
              </span>
            }
          />
        </dl>
      </Card>

      <ConfirmDialog
        isOpen={confirmPaid}
        onClose={() => setConfirmPaid(false)}
        onConfirm={handleMarkPaid}
        title={t('admin:tenantInvoices.confirm.markPaid.title')}
        message={t('admin:tenantInvoices.confirm.markPaid.message', {
          number: invoice.invoiceNumber || '',
        })}
        variant="info"
        loading={saving}
      />

      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancel}
        title={t('admin:tenantInvoices.confirm.cancel.title')}
        message={t('admin:tenantInvoices.confirm.cancel.message', {
          number: invoice.invoiceNumber || '',
        })}
        loading={saving}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-[12px] text-slate-500">{label}</dt>
      <dd className="text-[13px] text-slate-900 font-medium tabular-nums">{value}</dd>
    </div>
  );
}
